import { CartesianGrid, Line, LineChart, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import moment from 'moment';

import { linearRegression } from '../../../../../../helpers/utility-functions';
import { getChartLabelByDCM, getDataKeyByDCM } from '../../../../../utils';
import { FORTA_BLUE } from '../../../../../../utils/colors';
import { TP_GOAL_DATA_COLLECTION_METHODS, TRIAL_DATE_FORMAT } from '../constants';

import type { TargetChartProps } from '../types';

export default function TargetChart({ targetData }: TargetChartProps) {
  const dcm = targetData.dcm;
  const dataKey = getDataKeyByDCM(dcm);
  const trendKey = `${dataKey}_trend`;
  const isPercentage = dcm === TP_GOAL_DATA_COLLECTION_METHODS.PERCENTAGE;

  const dataPoints = targetData.trials
    .map((trial) => ({ x: moment(trial.date, TRIAL_DATE_FORMAT).unix(), y: trial[dataKey] }))
    .filter((point) => !isNaN(point.y));
  const hasTrend = dataPoints.length > 1;
  const regression = hasTrend ? linearRegression(dataPoints) : null;
  const firstX = dataPoints[0]?.x;
  const lastX = dataPoints[dataPoints.length - 1]?.x;

  const chartData = targetData.trials.map((trial) => {
    const x = moment(trial.date, TRIAL_DATE_FORMAT).unix();
    const isEdgePoint = x === firstX || x === lastX;
    return {
      ...trial,
      [trendKey]:
        regression && isEdgePoint ? regression.slope * x + regression.intercept : undefined,
    };
  });

  return (
    <ResponsiveContainer height={'99%'} width="99%" aspect={3}>
      <LineChart data={chartData} margin={{ top: 5, right: 10, left: 5, bottom: 30 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="date"
          label={{ value: 'Trial date', angle: 0, position: 'bottom', dy: 10 }}
          dy={10}
        />
        <YAxis
          label={{
            value: isPercentage ? '%' : getChartLabelByDCM(dcm),
            angle: isPercentage ? 0 : -90,
            position: 'insideLeft',
          }}
          tickFormatter={(value) => `${Math.round(value)}`}
          domain={isPercentage ? [0, 100] : [0, 'dataMax + 3']}
        />
        <Line
          type="monotone"
          dataKey={dataKey}
          stroke={FORTA_BLUE.primary}
          strokeWidth={2}
          activeDot={{ r: 3 }}
          isAnimationActive={false}
        />
        {hasTrend && (
          <Line
            connectNulls
            type="linear"
            dataKey={trendKey}
            stroke={FORTA_BLUE.primary}
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={false}
            activeDot={false}
            isAnimationActive={false}
          />
        )}
      </LineChart>
    </ResponsiveContainer>
  );
}
